import { useDraggable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import { formatDistanceToNow, formatRelative } from "date-fns";
import { MouseEvent } from "react";

import { useTheme } from "../../components/ui-v2";
import { useDrive } from "./DriveContext";
import { DriveGridItem } from "./DriveGridItem";
import DriveItem, { EllipsisButton } from "./DriveItem";
import { DriveAssetFragment } from "./queries.graphql";
import { DriveViewType } from "./types";
import { tableViewColumns } from "./utils";

type DriveAssetViewProps = {
  viewType: DriveViewType;
  asset: DriveAssetFragment;
  isSelected: boolean;
  isSelectable: boolean;
  onClick: (event: MouseEvent) => void;
  onDoubleClick: (event: MouseEvent) => void;
  onContextMenu: (event: MouseEvent) => void;
};

export default function DriveAssetView({
  viewType,
  asset,
  isSelected,
  isSelectable,
  onClick,
  onDoubleClick,
  onContextMenu,
}: DriveAssetViewProps) {
  const theme = useTheme();
  const { actionsDisabled } = useDrive();

  const {
    attributes,
    listeners,
    setNodeRef: draggableNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `asset__${asset.id}`,
    data: {
      supports: ["asset"],
      item: asset,
    },
  });
  const draggableStyle = transform
    ? {
        ...(isDragging && {
          pointerEvents: "none" as const, // this is necessary to prevent the link from being clicked while dragging
        }),
      }
    : undefined;

  const renderInner = () => {
    switch (viewType) {
      case "table":
        return (
          <>
            {tableViewColumns.map((column, index: number) => {
              return (
                <Box
                  key={column.id}
                  sx={{
                    width: column.width,
                    flexGrow: column.width ? 0 : 1,
                    flexShrink: column.width ? 0 : 1,
                    overflow: "hidden",
                    display: "-webkit-box",
                    "-webkit-box-orient": "vertical",
                    "-webkit-line-clamp": "1",
                    paddingRight: 4,
                    paddingLeft: 4,

                    ...(index > 1 && {
                      fontWeight: 300,
                      color: theme.palette.secondary[2],
                    }),
                  }}
                >
                  {column.assetValue?.(asset) || ""}
                </Box>
              );
            })}
            {!actionsDisabled && (
              <EllipsisButton onClick={onContextMenu} isSelected={isSelected} />
            )}
          </>
        );
      case "grid":
        return (
          <DriveGridItem
            fileType="canvas"
            src={asset.previewUrl ?? undefined}
            text={asset.name || "Untitled"}
            subText={formatDistanceToNow(asset.createdAt, {
              addSuffix: true,
            })}
            isSelected={isSelected}
            isDragging={isDragging}
            onContextMenu={onContextMenu}
            onFileClick={onClick}
            onFileDoubleClick={onDoubleClick}
          />
        );
    }
  };
  return (
    <DriveItem
      title={`${asset.name || "Untitled"} (created ${formatRelative(asset.createdAt, new Date())})`}
      type="Asset"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      draggableStyle={draggableStyle}
      isSelected={isSelected}
      isSelectable={isSelectable}
      isDragging={isDragging}
      viewType={viewType}
      ref={draggableNodeRef}
      {...listeners}
      {...attributes}
    >
      {renderInner()}
    </DriveItem>
  );
}
